import { CurrencyView } from '../currency-view';
import { Slider } from '../slider';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { resolveBaseToken } from '@/client/hooks/use-market-base-token';
import { handleKeyDownDisableNumber } from '@/client/utils/handle';
import { amountSchema } from '@/client/utils/schema';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatUnits, parseUnits } from 'viem';

type TreadInputProps = {
  disabled?: boolean;
  type: 'buy' | 'sell';
  value?: bigint;
  maxAmount?: bigint; // eth
  onValueChange?: (amount?: bigint) => void;
  isLoading?: boolean;
  decimal: number;
  baseTokenAddress?: string; // traded market's own base token
};

export const TreadInput = ({
  disabled,
  type,
  value,
  maxAmount,
  onValueChange,
  isLoading,
  decimal,
  baseTokenAddress,
}: TreadInputProps) => {
  const { t } = useTranslation();
  const initValue = useMemo(() => (value ? formatUnits(value, decimal) : ''), [decimal, value]);
  // No useMemo — must always reflect latest maxAmount and decimal to avoid stale display
  const maxValue = maxAmount != null ? formatUnits(maxAmount, decimal) : 0;
  const [inputAmount, setInputAmount] = useState<string>(initValue);
  useEffect(() => {
    if (inputAmount !== initValue) setInputAmount(initValue);
  }, [initValue]);
  const quickerInputs = [
    {
      label: '+1',
      value: 1,
      onClick: () => {
        const amount = `${Number(inputAmount ?? 0) + 1}`;
        setInputAmount(amount);
      },
    },
    {
      label: '+10',
      value: 10,
      onClick: () => {
        const amount = `${Number(inputAmount ?? 0) + 10}`;
        setInputAmount(amount);
      },
    },
    {
      label: t('max'),
      value: maxValue,
      onClick: () => {
        const amount = maxValue ? Math.floor(Number(maxValue) * 1e4) / 1e4 : '';
        setInputAmount(amount?.toString());
      },
    },
  ];
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const { success } = amountSchema.safeParse(input);
    if (success || input == '') {
      const decimalParts = input.split('.');
      if (decimalParts.length > 1 && decimalParts[1].length > 2) return;
      setInputAmount(input);
    }
  };

  useEffect(() => {
    onValueChange?.(inputAmount ? parseUnits(inputAmount, decimal) : undefined);
  }, [decimal, inputAmount, onValueChange]);

  return (
    <>
      <div className="mt-2 pos-relative">
        <Input
          type="text"
          disabled={disabled}
          value={inputAmount}
          onKeyDown={handleKeyDownDisableNumber}
          onChange={handleChange}
          placeholder="0"
          className="border border-foreground/10 focus:border-foreground/40 py-4 h-14 !text-8 leading-8 placeholder:leading-8 placeholder:text-8 pr-33 shadow-none"
        />
        <div className="pos-absolute top-3 flex items-center space-x-2 right-2">
          {quickerInputs.map((item, index) => (
            <Button
              key={`quicker-input-${index}`}
              variant="secondary"
              size="sm"
              className="!rounded-2 min-w-8 p-2 bg-foreground/4 text-foreground/70 hover:bg-primary hover:text-primary-foreground"
              disabled={disabled}
              onClick={item.onClick}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 space-x-8">
        <div className="flex items-center space-x-1 flex-1 flex-shrink-0 leading-3 text-sm  text-foreground/70 ">
          <span className="whitespace-nowrap">
            {type == 'buy' &&
              t('token-balance', '{{symbol}} 餘額', { symbol: resolveBaseToken(baseTokenAddress)?.symbol ?? '' })}
            {type == 'sell' && t('shares-held', '持有份額')}
          </span>
          <CurrencyView
            showIcon={type === 'buy'}
            animated={true}
            value={maxValue}
            isLoading={isLoading}
            decimal={0}
            baseTokenAddress={baseTokenAddress}
          />
        </div>
        <Slider
          className="w-33"
          disabled={disabled}
          value={Number(inputAmount)}
          min={0}
          max={Number(maxValue)}
          setValue={value => setInputAmount(`${Math.floor(Number(value) * 1e2) / 1e2}`)}
        />
      </div>
    </>
  );
};
